import React, { type FC } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import PersonalInfoReview from './PersonalInfoReview';
import AdditionalInfoReview from './AdditionalInfoReview';
import ShoppingCartReview from './ShoppingCartReview';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import { UiButton, UiHStack, UiIconCaretLeft, UiIconCaretRight, UiStack } from '@/lib/ui';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { type RegistrationCompleteRequest, RegistrationStrategyType, type Registration, completeRegistration, completeRegistrationUpsell, type RegistrationUpsellCompleteRequest, listOrdersQueryKey, registrationQueryKey, cartQueryKey } from '@/api/registration';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type ApiResponseSingle } from '@/api/tenantClient';
import OrderReview from './OrderReview';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import InfoBox from '@/registration/component/InfoBox';
import { useRegisterUpsellSteps } from '@/registration/hook/useRegisterUpsellSteps';
import { useUserSession } from '@/registration/hook/useUserSession';
import { isCurrencyValueZero } from '@/registration/utils/currencyUtils';
import BaseThemeButton from '@/base/ThemeButton';
interface ReviewProps {
  isUpsell?: boolean
}

const Review: FC<ReviewProps> = ({ isUpsell }) => {
  const { eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const { createTenantApiRequest } = useTenantApi();
  const cartQuery = useCartQuery(eventId);
  const queryClient = useQueryClient();
  const { userSession } = useUserSession();
  const { toNextStep: defaultToNextStep, toPreviousStep: defaultToPreviousStep } = useRegisterSteps();
  const { toNextStep: upsellToNextStep, toPreviousStep: upsellToPreviousStep } = useRegisterUpsellSteps();

  const toNextStep = isUpsell ? upsellToNextStep : defaultToNextStep;
  const toPreviousStep = isUpsell ? upsellToPreviousStep : defaultToPreviousStep;

  const { mutate, isLoading } = useMutation<ApiResponseSingle<Registration>, Error, RegistrationCompleteRequest>(
    {
      mutationFn: async (data: RegistrationCompleteRequest) => {
        return await completeRegistration(createTenantApiRequest)(data);
      },
      onSuccess: async (data: ApiResponseSingle<Registration>) => {
        if (data.item) {
          await queryClient.invalidateQueries({ queryKey: [registrationQuery] });
          toNextStep();
        } else {
          throw new Error('Failed to complete registration');
        }
      },
      onError: (error: any) => {
        throw error;
      }
    }
  );

  const { mutate: mutateUpsell, isLoading: isLoadingUpsell } = useMutation<ApiResponseSingle<Registration>, Error, RegistrationUpsellCompleteRequest>(
    {
      mutationFn: async (data: RegistrationUpsellCompleteRequest) => {
        return await completeRegistrationUpsell(createTenantApiRequest)(data);
      },
      onSuccess: async (data: ApiResponseSingle<Registration>) => {
        if (data.item) {
          await queryClient.invalidateQueries({ queryKey: [registrationQuery] });
          await queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
          await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
          await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: registrationQuery?.data?.id }] });

          toNextStep();
        } else {
          throw new Error('Failed to complete registration');
        }
      },
      onError: (error: any) => {
        throw error;
      }
    }
  );

  const handleCompleteRegistration = () => {
    if (registrationQuery?.data && !isUpsell) {
      mutate({ registrationId: registrationQuery.data.id });
    } else if (registrationQuery?.data && isUpsell) {
      mutateUpsell({ registrationId: registrationQuery.data.id });
    }
  };

  const isFormsFirstAndFree = (registration: Registration, totalAmount: number) => {
    return registration.registrationStrategy === RegistrationStrategyType.FORMS_FIRST && isCurrencyValueZero(totalAmount.toString());
  };

  return (
    <RegisterLayout>
      <QueryContainer query={registrationQuery}>
        {(registration) => {
          return (
            <QueryContainer query={cartQuery}>
              {(cart) => {
                return (
                  <>
                    <BaseTransitionSlideLeft>
                      <UiStack flexGrow={1} spacing={8} pb={16}>
                        <InfoBox
                          title={'Registration review'}
                          // description={eventSetting?.pageMetadata?.personalInfo?.description}
                        />
                        {/*<UiHStack>*/}
                        {/*  <UiIconInfo color={'green.400'} size={'3xl'}/>*/}
                        {/*  <UiText variant={'h6'}>Registration review</UiText>*/}
                        {/*</UiHStack>*/}
                        {/* <BaseDividerHorizontal height={{base: 4, lg: 6}} color={'gray.300'}/> */}
                        <PersonalInfoReview />
                        {registration.registrationSteps?.find(value => { return value.routeName === 'RegistrationRegisterAdditionalInfo'; }) && (
                          <AdditionalInfoReview />
                        )}
                        {registration.groupRegistrationId == null && (
                          <>
                            {registration.registrationStrategy === RegistrationStrategyType.FORMS_FIRST && (
                              <ShoppingCartReview />
                            )}
                            {registration.registrationStrategy === RegistrationStrategyType.PAYMENT_FIRST && (
                              <OrderReview />
                            )}
                          </>
                        )}
                      </UiStack>
                    </BaseTransitionSlideLeft>
                    <UiStack height={96} />
                    <LayoutFooter>
                      <UiHStack justifyContent={'space-between'} flexGrow={1}>
                        {/*<UiButton*/}
                        {/*  size={'lg'}*/}
                        {/*  colorScheme={'primary'}*/}
                        {/*  onClick={toPreviousStep}*/}
                        {/*  variant={'ghost'}*/}
                        {/*  fontSize={'lg'}*/}
                        {/*  leftIcon={(*/}
                        {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*  )}*/}
                        {/*>*/}
                        {/*  Previous*/}
                        {/*</UiButton>*/}
                        <BaseThemeButton
                          theme={'gray'}
                          onClick={toPreviousStep}
                          leftIcon={(
                            <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          Prev
                        </BaseThemeButton>
                        {(registration.registrationStrategy === RegistrationStrategyType.FORMS_FIRST && !isFormsFirstAndFree(registration, cart.totalAmount)) && (
                          <BaseThemeButton
                            theme={'primary'}
                            onClick={toNextStep}
                            rightIcon={(
                              <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                            )}
                          >
                            Next
                          </BaseThemeButton>
                        )}
                        {(registration.registrationStrategy === RegistrationStrategyType.PAYMENT_FIRST || isFormsFirstAndFree(registration, cart.totalAmount)) && (
                          <BaseThemeButton
                            theme={'primary'}
                            onClick={handleCompleteRegistration}
                            isLoading={isLoading || isLoadingUpsell}
                            rightIcon={(
                              <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                            )}
                          >
                            Next
                          </BaseThemeButton>
                        )}

                        {/*{(registration.registrationStrategy === RegistrationStrategyType.FORMS_FIRST && !isFormsFirstAndFree(registration, cart.totalAmount)) && (*/}
                        {/*  <UiButton*/}
                        {/*    size={'lg'}*/}
                        {/*    colorScheme={'primary'}*/}
                        {/*    onClick={toNextStep}*/}
                        {/*    variant={'ghost'}*/}
                        {/*    fontSize={'lg'}*/}
                        {/*    rightIcon={(*/}
                        {/*      <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*    )}*/}
                        {/*  >*/}
                        {/*    Next*/}
                        {/*  </UiButton>*/}
                        {/*)}*/}
                        {/*{(registration.registrationStrategy === RegistrationStrategyType.PAYMENT_FIRST || isFormsFirstAndFree(registration, cart.totalAmount)) && (*/}
                        {/*  <UiButton*/}
                        {/*    size={'lg'}*/}
                        {/*    colorScheme={'primary'}*/}
                        {/*    onClick={handleCompleteRegistration}*/}
                        {/*    isLoading={isLoading || isLoadingUpsell}*/}
                        {/*    variant={'ghost'}*/}
                        {/*    fontSize={'lg'}*/}
                        {/*    rightIcon={(*/}
                        {/*      <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*    )}*/}
                        {/*  >*/}
                        {/*    Complete registration*/}
                        {/*  </UiButton>*/}
                        {/*)}*/}
                      </UiHStack>
                    </LayoutFooter>
                  </>
                );
              }}
            </QueryContainer>
          );
        }}
      </QueryContainer>
    </RegisterLayout>
  );
};

export default Review;
